/**
 * Currency Context
 * Provides currency state and conversion functions throughout the app
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCurrencyPreference, saveCurrencyPreference, clearCurrencyCache, getInitialCurrency } from '../services/currencyService';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Initialize with user's saved currency to prevent USD flicker
  const [currency, setCurrency] = useState(() => getInitialCurrency());
  const [rate, setRate] = useState({ rate: 1, rateWithMarkup: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load currency preference on mount
  useEffect(() => {
    loadCurrencyPreference();
    
    // Listen for user changes (login/logout) via custom event
    const handleUserChange = (e) => {
      console.log('👤 [CurrencyContext] User changed event received:', e.detail);
      const newCurrency = getInitialCurrency();
      setCurrency(newCurrency);
      loadCurrencyPreference(true); // Force refresh
    };
    
    window.addEventListener('userChanged', handleUserChange);
    
    // Listen for FX markup changes from admin panel
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('fx_updates');
      
      channel.onmessage = (event) => {
        if (event.data.type === 'MARKUP_CHANGED') {
          console.log('🔔 [CurrencyContext] Markup changed, refreshing rates...');
          clearCurrencyCache();
          loadCurrencyPreference(true); // Force refresh
        }
      };
      
      return () => {
        window.removeEventListener('userChanged', handleUserChange);
        channel.close();
      };
    }
    
    return () => {
      window.removeEventListener('userChanged', handleUserChange);
    };
  }, []);

  // Auto-refresh rates every 5 minutes to pick up markup changes
  useEffect(() => {
    // Only auto-refresh for non-USD currencies
    if (currency === 'USD') return;

    console.log('⏰ [CurrencyContext] Setting up auto-refresh every 5 minutes for', currency);
    
    const intervalId = setInterval(() => {
      console.log('🔄 [CurrencyContext] Auto-refreshing rates (5-minute interval)');
      clearCurrencyCache();
      loadCurrencyPreference(true); // Force refresh to get latest markup
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      console.log('⏹️ [CurrencyContext] Clearing auto-refresh interval');
      clearInterval(intervalId);
    };
  }, [currency]);

  const loadCurrencyPreference = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 [CurrencyContext] Loading currency preference...', forceRefresh ? '(FORCE REFRESH)' : '');
      const { currency: userCurrency, rate: userRate } = await getCurrencyPreference(true, forceRefresh);
      console.log('✅ [CurrencyContext] Loaded preference:', { currency: userCurrency, rate: userRate });
      setCurrency(userCurrency);
      setRate(userRate);
    } catch (err) {
      console.error('❌ [CurrencyContext] Failed to load currency preference:', err);
      setError(err.message);
      // Fallback to USD
      setCurrency('USD');
      setRate({ rate: 1, rateWithMarkup: 1 });
    } finally {
      setLoading(false);
    }
  };

  const changeCurrency = useCallback(async (newCurrency) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 [CurrencyContext] Changing currency to:', newCurrency);
      const { rate: newRate } = await saveCurrencyPreference(newCurrency);
      console.log('✅ [CurrencyContext] Currency changed:', { currency: newCurrency, rate: newRate });
      setCurrency(newCurrency);
      setRate(newRate);
      return true;
    } catch (err) {
      console.error('❌ [CurrencyContext] Failed to change currency:', err);
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const convert = useCallback((amountUSD) => {
    if (!amountUSD || currency === 'USD' || !rate) {
      return amountUSD;
    }
    const converted = amountUSD * rate.rateWithMarkup;
    console.log(`💱 [CurrencyContext] Converting $${amountUSD} USD to ${currency}:`, {
      rate: rate.rateWithMarkup,
      converted: converted
    });
    return converted;
  }, [currency, rate]);

  const formatPrice = useCallback((amount, curr = currency) => {
    const symbols = {
      USD: '$',
      SGD: 'S$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      AUD: 'A$',
      HKD: 'HK$',
      CNY: '¥',
      AED: 'د.إ',
      INR: '₹'
    };

    const symbol = symbols[curr] || curr;

    // JPY doesn't use decimals
    if (curr === 'JPY') {
      return `${symbol}${Math.round(amount).toLocaleString()}`;
    }

    return `${symbol}${amount.toFixed(2)}`;
  }, [currency]);

  const refresh = useCallback(async (forceRefresh = true) => {
    console.log('🔄 [CurrencyContext] Refresh called, force:', forceRefresh);
    clearCurrencyCache();
    await loadCurrencyPreference(forceRefresh);
  }, []);

  const value = {
    currency,
    rate,
    loading,
    error,
    changeCurrency,
    convert,
    formatPrice,
    refresh,
    isUSD: currency === 'USD'
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
